import { useCallback, useMemo, useState } from "react";
import { useChat, Chat } from "@ai-sdk/react";
import { useUser } from "@/hooks/useUser";
import { useNotification } from "@/hooks/useNotification";
import { getMessages } from "instantdb/client/message";
import { type ChatStatus, type UIMessage, DefaultChatTransport } from "ai";
import { getChatInstance } from "@/lib/chatInstances";
import { convertToUIMessage } from "@/utilities/message-helpers";
import { useSetAtom, useAtomValue } from "jotai";
import { loadingChatIdsAtom } from "@/state/chatAtoms";
import { customerAtom } from "@/state/customerAtoms";
import { getFeatureId } from "@/lib/getFeatureId";
import { isAdvancedModel, imageModels } from "@/lib/aiproviders/models";

type UseAIChatReturn = {
  messages: UIMessage[];
  status: ChatStatus;
  isLoading: boolean;
  isPending: boolean;
  sendToAI: (content: string, model: string) => Promise<void>;
  stop: () => void;
};

export const useAIChat = ({
  chatId,
  model,
}: {
  chatId?: string;
  model: string;
}): UseAIChatReturn => {
  const { userId, hasPro } = useUser();
  const customer = useAtomValue(customerAtom);
  const setLoadingChatIds = useSetAtom(loadingChatIdsAtom);
  const [isPending, setIsPending] = useState(false);
  const { showNotification } = useNotification();
  const isAdvanced = isAdvancedModel(model);

  // Get messages data from database
  const { messages: dbMessages, isLoading: isLoadingMessages } = getMessages({
    chatId,
  });

  // Convert database messages to UIMessage format
  const initialMessages = dbMessages.map(convertToUIMessage);

  // Define onFinish callback - stable reference
  const onFinishCallback = useCallback(
    async (message: any) => {
      // Clear pending state when response is complete
      setIsPending(false);

      // Clear loading state when AI response is complete
      if (chatId) {
        setLoadingChatIds((prev) => {
          const next = new Set(prev);
          next.delete(chatId);
          return next;
        });
      }
    },
    [chatId, setLoadingChatIds],
  );

  // Define onError callback - stable reference
  const onErrorCallback = useCallback(
    (error: Error) => {
      // Clear pending state when error occurs
      setIsPending(false);

      // Clear loading state when AI fails
      if (chatId) {
        setLoadingChatIds((prev) => {
          const next = new Set(prev);
          next.delete(chatId);
          return next;
        });
      }

      // Show error toast
      showNotification({
        message: "AI generation failed. Please try again.",
        type: "error",
      });
    },
    [chatId, setLoadingChatIds, showNotification],
  );

  // Prepare transport config with customerId and featureId
  const transportConfig = useMemo(() => {
    const customerId = customer?.id || userId;
    const featureId = getFeatureId(hasPro, isAdvanced);

    return {
      api: "/api/chat",
      prepareSendMessagesRequest: (options: any) => {
        return {
          ...options,
          body: {
            ...options.body,
            messages: options.messages,
            customerId,
            featureId,
            chatId,
          },
        };
      },
    };
  }, [customer?.id, userId, hasPro, model, chatId]);

  // Get or create singleton chat instance
  const chat = useMemo(() => {
    if (!chatId) {
      // Create a dummy chat instance when no chatId
      return new Chat({
        id: "dummy-no-chat",
        messages: [],
        transport: new DefaultChatTransport(transportConfig),
      });
    }
    return getChatInstance(
      chatId,
      initialMessages,
      onFinishCallback,
      onErrorCallback,
      transportConfig,
    );
  }, [chatId, initialMessages, onFinishCallback, onErrorCallback, transportConfig]);

  // Always call useChat with a chat instance
  const { messages, sendMessage, status, stop } = useChat({ chat, experimental_throttle: 50 });

  // Helper function to check if model is an image model
  const isImageModel = useCallback((model: string) => {
    return imageModels.some((m) => m.id === model);
  }, []);

  // Generate image using the image API
  const generateImage = useCallback(
    async (prompt: string, model: string) => {
      try {
        const response = await fetch("/api/image", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            prompt,
            model,
            userId,
            chatId,
            customerId: customer?.id || userId,
            featureId: getFeatureId(hasPro, isAdvancedModel(model)),
          }),
        });

        if (!response.ok) {
          let errorMessage = `Image generation failed: ${response.statusText}`;

          // Show error toast
          showNotification({
            message: "AI image generation failed. Please try again.",
            type: "error",
          });

          throw new Error(errorMessage);
        }

        // Parse the JSON response
        const result = await response.json();

        // Clear loading state when image generation completes successfully
        if (chatId) {
          setLoadingChatIds((prev) => {
            const next = new Set(prev);
            next.delete(chatId);
            return next;
          });
        }

        return result;
      } catch (error) {
        console.error("Image generation error:", error);
        // Clear loading state on error as well
        if (chatId) {
          setLoadingChatIds((prev) => {
            const next = new Set(prev);
            next.delete(chatId);
            return next;
          });
        }
        throw error;
      }
    },
    [userId, chatId, setLoadingChatIds, customer?.id, hasPro, showNotification],
  );

  const sendToAI = useCallback(
    async (content: string, selectedModel: string) => {
      // Ensure we reset and then set pending state for immediate UI feedback
      setIsPending(true);

      try {
        if (isImageModel(selectedModel)) {
          // Handle image generation - API route creates the assistant message
          await generateImage(content, selectedModel);
          // No need to call sendMessage as the API route handles message creation
        } else {
          // Handle text generation using existing flow
          await sendMessage({
            text: content,
            metadata: {
              model: selectedModel,
            },
          });
        }
      } catch (error) {
        setIsPending(false);
        onErrorCallback(error as Error);
      } finally {
        setIsPending(false);
      }
    },
    [sendMessage, isImageModel, generateImage, onErrorCallback],
  );

  const stopStreaming = useCallback(() => {
    stop();
    setIsPending(false);

    // Clear loading state for this chat
    if (chatId) {
      setLoadingChatIds((prev) => {
        const next = new Set(prev);
        next.delete(chatId);
        return next;
      });
    }
  }, [stop, chatId, setLoadingChatIds]);

  // Determine which messages to display based on streaming status
  let displayMessages = initialMessages || [];

  if (status === "streaming" && messages.length > 0) {
    // While streaming, append the streaming assistant message to database messages
    const lastMessage = messages[messages.length - 1];

    // Only add the streaming message if it's an assistant message
    if (lastMessage.role === "assistant") {
      displayMessages = [...(initialMessages || []), lastMessage];
    }
  }

  return {
    messages: displayMessages,
    status,
    isLoading: isLoadingMessages,
    isPending,
    sendToAI,
    stop: stopStreaming,
  };
};
